import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import RequirementUploadForm from './RequirementUploadForm';
import { EmptyState, ErrorState, LoadingState } from '../ui';

/**
 * DEFINE · Imported Requirements — RT2 upload panel.
 */
const RequirementUploadPage = ({ projectId: projectIdProp, embedded = false } = {}) => {
  const params = useParams();
  const { selectedProject } = useProject();
  const { getProjectIdFromURL } = useWorkspaceRouting();
  const project_id =
    projectIdProp ||
    params.project_id ||
    getProjectIdFromURL() ||
    selectedProject?.id ||
    null;
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!project_id) {
        setFetchError('Project id is missing');
        setLoading(false);
        return;
      }

      try {
        const response = await axiosInstance.get(`/projects/get_project?project_id=${project_id}`);
        const data = response?.data || response;
        if (!data) throw new Error('Project not found');

        setProject({
          id: parseInt(project_id, 10),
          name: data.project_name || 'Project',
          description: data.description || '',
          type: data.project_type || 'None',
          status: data.status || 'active',
          originalData: data,
        });
        setFetchError(null);
      } catch (err) {
        console.error('Error fetching project details:', err);
        setFetchError(err?.message || 'Failed to load project details');
        setProject(null);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchProjectDetails();
  }, [project_id]);

  const handleBackToProjects = () => {
    navigate('/dashboard/projects');
  };

  const integrationLabel = project?.type === 'project_pulse' ? 'WorkXiq' : 'Jira';

  if (loading) {
    return <LoadingState label="Loading project details…" />;
  }

  if (!project_id) {
    return (
      <EmptyState
        title="No project selected"
        description="Select a project to upload requirements."
      />
    );
  }

  if (fetchError || !project) {
    return (
      <ErrorState
        title="Unable to load project"
        description={fetchError || 'Project details are unavailable.'}
        onRetry={embedded ? undefined : handleBackToProjects}
        retryLabel="Back to Projects"
      />
    );
  }

  return (
    <div className={`rt-define-panel rt-define-upload${embedded ? ' rt-define-panel--embed' : ''}`}>
      {!embedded && (
        <div className="rt-define-panel__intro">
          <button type="button" className="rt-btn rt-btn--tertiary rt-btn--sm" onClick={handleBackToProjects}>
            ← Projects
          </button>
          <h1 className="rt-define-panel__title">Upload Requirements for {project.name}</h1>
          <p className="rt2-muted">
            Upload a requirements document to create epics and stories in {integrationLabel}.
          </p>
        </div>
      )}

      {embedded && (
        <div className="rt-define-panel__intro rt-define-panel__intro--compact">
          <p className="rt2-muted">
            Upload PDF or DOCX requirements for <strong>{project.name}</strong>. Epics and stories are
            created in {integrationLabel} when processing finishes.
          </p>
        </div>
      )}

      <div className="rt-define-upload__card">
        <RequirementUploadForm
          project={project}
          variant="page"
          onCancel={embedded ? undefined : handleBackToProjects}
          onSuccess={embedded ? undefined : handleBackToProjects}
        />
      </div>
    </div>
  );
};

export default RequirementUploadPage;
